// Angular-Module
import {Injectable} from '@angular/core';
// ReactiveX for JavaScript
import {Observable, of, forkJoin, map} from 'rxjs';
// Globale Services
import {AppCoreService} from '@global/services/app-core.service';
import {BackendService} from '@global/services/backend.service';
// Interfaces für Structured Objects einbinden
import {LooseObject} from '@shared/loose-object';
import {EntityAffiliation} from '@shared/entity-affiliation';

@Injectable({providedIn: 'root'})
export class EntityAffiliationService {
    /**
     * Konstruktor (inkl. dependency injection)
     * @param appCore
     * @param backendService
     */
    constructor(private appCore: AppCoreService, private backendService: BackendService) {}

    /**
     * @param id
     * @brief   Stammdaten einer Einrichtung laden
     * @returns  Observable<any>
     */
    loadData(id: number, hideEntityAffiliationForRoles: any): Observable<any> {
        // 1. Return an empty observable instead of null to prevent subscriber crashes
        if (id == null) {
            return of(null); 
        }

        // 2. Build the base filter
        const parentsFilter: any = { child_key: id };
        const childrenFilter: any = { parent_key: id };

        // 3. Conditionally add the relationship exclusion
        if (hideEntityAffiliationForRoles && hideEntityAffiliationForRoles.length > 0) {
            parentsFilter['relationship_type NOT IN'] = hideEntityAffiliationForRoles;
            childrenFilter['relationship_type NOT IN'] = hideEntityAffiliationForRoles;
        }

        // 4. Construct URLs
        const url = '/crud/EntityAffiliation/read';

        const parentsRequest$ = this.backendService.postRequest(url, { filter: parentsFilter });
        const childrenRequest$ = this.backendService.postRequest(url, { filter: childrenFilter });

        return forkJoin([parentsRequest$, childrenRequest$]).pipe(
            map(([parent, child]) => ({
                parent,
                child,
            })),
        );
    }

    /**
     * @param formData
     * @param id
     * @brief   Daten einer Verknüpfung speichern
     * @returns  Observable<any>
     */
    saveData(formData: LooseObject, id: string): Observable<any> {
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.postRequest(`/crud/EntityAffiliation/edit/${id}`, formData);
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    crudCreateData(data: EntityAffiliation): Observable<any> {
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.postRequest('/crud/EntityAffiliation/create/', data);
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    /**
     * @param formData
     * @brief   Daten einer Verknüpfung löschen
     * @returns  Observable<any>
     */
    deleteData(formData: LooseObject): Observable<any> {
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.postRequest('EntityAffiliation/delete/', formData);
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }

    /**
     * @param formData
     * @brief   Daten einer Verknüpfung löschen
     * @returns  Observable<any>
     */
    crudDeleteData(id: string): Observable<any> {
        // POST-Request über BackendService senden
        const postRequest$: Observable<any> = this.backendService.deleteRequest(`/crud/EntityAffiliation/delete/${id}`, {id});
        // Observable (an Komponente) zurücklieferen
        return postRequest$;
    }
}
